import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Input,
  InputNumber,
  Link,
  Switch,
  Table,
  Form,
  Select,
  MessagePlugin,
} from 'tdesign-react';
import { produce } from 'immer';
import ModalHeader from '@/components/Header/Modal';
import {
  createFunction,
  doFunctionRestart,
  doFunctionStop,
  getFunctionChart,
  getFunctionConfigDetail,
  getFunctionInstances,
  getFunctionList,
  getFunctionParams,
  getFunctionStatus,
  getPackageList,
  updateFunctionConfig,
} from '@/services/functionService';
import { getClusterResources } from '@/services/permissionsService';
import useTable from '@/hooks/useTable';
import ShadowCard from '@/components/ShadowCard';
import { isValidJSONRC, isValidSimpleJSONRC } from '@/utils/common';
import PermWrapper from '@/components/PermWrapper';
import ColTip from '@/components/Tip';
import JSONConverterInput from '@/components/Form/JSONConverterInput';
import RCForm from '@/components/Form';
import Options from '../../components/Options';
import { defaultColumns } from '../../identical';
import FormModal from '../../components/FormModal';
import TNItems from '../../components/TNItems';
import TopicsInput from '../../components/TopicsInput';
import Detail from './Detail';
import Instance from './Instance';

const { FormItem } = RCForm;

const biggerThanZero = (rule, value) => {
  if (value < 0) {
    return Promise.reject('不能小于0');
  } else {
    return Promise.resolve();
  }
};

export default function Body() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [topicOptions, setTopicOptions] = useState([]);

  const clusterName = searchParams.get('name');

  const loadTopicOptions = useMemoizedFn(async () => {
    const { data = [] } = await getClusterResources({
      clusterName,
      type: 'topic',
    });
    setTopicOptions(
      data.map((item) => ({
        label: item,
        value: item,
      })),
    );
  }, []);

  useEffect(() => {
    loadTopicOptions();
  }, [loadTopicOptions, clusterName]);

  // 操作项里的各种请求
  const getOptionRequests = (row) => ({
    // 看板
    getChartsData: (params) =>
      getFunctionChart({
        clusterName,
        namespace: `${row.tenant}/${row.namespace}`,
        functionName: row.functionName,
        ...params,
      }),
    // 包管理列表
    getPackageList: (val) =>
      getPackageList({
        clusterName,
        packageType: 'Function',
        functionRuntime: val,
      }),
    getConfigDetail: async () => {
      const { data } = await getFunctionConfigDetail({
        clusterName,
        functionName: row.functionName,
      });
      return { ...data, userConfig: data.userConfig ? JSON.stringify(data.userConfig) : null };
    },
    updateFunctionConfig: (values) =>
      updateFunctionConfig({
        clusterName,
        ...values,
        userConfig: JSON.parse(values.userConfig || null),
      }),
    doRestart: () =>
      doFunctionRestart({
        clusterName,
        functionName: row.functionName,
      }),
    doStop: () =>
      doFunctionStop({
        clusterName,
        functionName: row.functionName,
      }),
    getStatus: () => getFunctionStatus({ clusterName, functionName: row.functionName }),
    getParams: () => getFunctionParams({ clusterName, functionName: row.functionName }),
  });

  // 自定义信息
  const getDiffNode = (row, isEdit, form) => ({
    dashboardHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.functionName}`, clusterName]}>
        监控-Function
      </ModalHeader>
    ),
    packageHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.functionName}`, clusterName]}>
        包管理
      </ModalHeader>
    ),
    startHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.functionName}`, clusterName]}>
        启动
      </ModalHeader>
    ),
    startFormSnipPart1: (
      <>
        <FormItem rules={[{ required: true }]} name='className' label='函数入口'>
          <Input />
        </FormItem>
        <FormItem
          rules={[{ required: true, message: '输入Topic必填' }]}
          name='inputs'
          label={<ColTip content='该参数不支持更新！'>输入Topic</ColTip>}
          tips={isEdit && '该参数不支持更新！'}
        >
          <TopicsInput clusterName={clusterName} disabled={isEdit} placeholder='允许选择多条' />
        </FormItem>
        <FormItem name='output' label='输出Topic'>
          <Select
            options={topicOptions}
            onChange={(value) => {
              if (form.getFieldValue('inputs').split(',').includes(value)) {
                MessagePlugin.warning('输入Topic不能和输出Topic相同！');
                form.setFieldsValue({
                  output: undefined,
                });
              }
            }}
            placeholder='单个Topic'
            clearable
            filterable
          />
        </FormItem>
        <FormItem
          name='subscriptionName'
          rules={[
            {
              pattern: /^(([a-z0-9][-a-z0-9]*)?[a-z0-9])?$/,
              message: '小写字母、数字和中划线组成，中划线不能首尾',
            },
          ]}
          label={<ColTip content='订阅名称不填写，将随机生成'>订阅名称</ColTip>}
        >
          <Input placeholder='subscription名称' />
        </FormItem>
      </>
    ),
    startFormSnipJava: (
      <FormItem label='输入输出Schema'>
        <Block>
          <FormItem name='inputTypeClassName' label='inputTypeClassName' labelWidth={200}>
            <Input placeholder='如：java.lang.Byte[]' />
          </FormItem>
          <FormItem name='outputTypeClassName' label='outputTypeClassName' labelWidth={200}>
            <Input placeholder='如：java.lang.Byte[]' />
          </FormItem>
        </Block>
      </FormItem>
    ),
    startFormSnipPart2: (
      <>
        <FormItem
          label={
            <ColTip
              content={
                <>
                  <strong>用户参数</strong>
                  <li>传入JSON，可调用context.getUserConfigValue()获取。</li>
                </>
              }
            >
              用户参数
            </ColTip>
          }
          name='userConfig'
          rules={[{ validator: isValidJSONRC, message: '请输入json格式' }]}
        >
          <JSONConverterInput jsonOnly />
        </FormItem>
        <FormItem
          label={
            <ColTip
              content={
                <>
                  开启该配置需要确保当前Function任务是WindowsFunction类型，详情可以参考
                  <Link
                    theme='primary'
                    target='_blank'
                    href='https://pulsar.apache.org/docs/next/window-functions-context/'
                  >
                    官方文档
                  </Link>
                  。
                </>
              }
            >
              是否窗口任务
            </ColTip>
          }
          name='isWindowFunction'
        >
          <Switch />
        </FormItem>
        <FormItem dependencies={['isWindowFunction']}>
          {() =>
            form.getFieldValue('isWindowFunction') && (
              <>
                <FormItem
                  rules={[
                    { required: true },
                    { validator: biggerThanZero, message: '请输入大于0的值' },
                  ]}
                  name='slidingIntervalDurationMs'
                  label='窗口滑动间隔'
                >
                  <InputNumber
                    theme='normal'
                    decimalPlaces={0}
                    min={1}
                    placeholder='填写正整数'
                    suffix='ms'
                  />
                </FormItem>

                <FormItem
                  rules={[
                    { required: true },
                    { validator: biggerThanZero, message: '请输入大于0的值' },
                  ]}
                  name='windowLengthDurationMs'
                  label='窗口计算间隔'
                >
                  <InputNumber
                    theme='normal'
                    decimalPlaces={0}
                    min={1}
                    placeholder='填写正整数'
                    suffix='ms'
                  />
                </FormItem>
              </>
            )
          }
        </FormItem>
      </>
    ),
    startFormSnipPart3: (
      <>
        <FormItem
          label={
            <ColTip
              content={
                <>
                  <strong>生产端参数-默认不需要配置</strong>
                  <li>1、往下游进行批量生产时，构造生产对象所需的参数，支持配置参数如下：</li>
                  <li>2、maxPendingMessages（单分区），默认值为1000。</li>
                  <li>3、maxPendingMessagesAcrossPartitions（多分区），默认值为50000。</li>
                  <li>4、其它核心参数不允许配置的默认值如下：</li>
                  <li>5、blockIfQueueFull为true。</li>
                  <li>6、sendTimeout为0，永不超时。</li>
                  <li>7、compressionType为CompressionType.LZ4。</li>
                </>
              }
            >
              生产端参数
            </ColTip>
          }
          name='producerConfig'
          rules={[
            {
              validator: isValidSimpleJSONRC,
              message: '请输入[key, value]结构的json格式，不允许嵌套',
            },
          ]}
        >
          <JSONConverterInput />
        </FormItem>
        <FormItem
          name='forwardSourceMessageProperty'
          label={<ColTip content={'forwardSourceMessageProperty'}>转发源消息属性</ColTip>}
        >
          <Switch />
        </FormItem>
        <FormItem
          name='maxPendingAsyncRequests'
          label={<ColTip content={'maxPendingAsyncRequests'}>最大异步请求</ColTip>}
        >
          <InputNumber min={1} decimalPlaces={0} placeholder='填写正整数' theme='normal' />
        </FormItem>
      </>
    ),
  });

  const columns = produce(defaultColumns, (draft) => {
    // 第一列
    const taskColumn = draft[0];
    taskColumn.colKey = 'functionName';
    taskColumn.cell = ({ row }) => (
      <Detail row={row} clusterName={clusterName} onRefresh={doRefresh} />
    );

    // 实例个数列
    const instanceNumColumn = draft.find((item) => item.colKey === 'instanceNum');
    instanceNumColumn.cell = ({ row }) => (
      <Instance
        row={row}
        request={() => {
          return getFunctionInstances({
            clusterName,
            functionName: row.functionName,
          });
        }}
      />
    );

    // 操作列
    const optionColumn = draft.find((item) => item.colKey === 'option');
    optionColumn.cell = ({ row }) => (
      <Options
        row={row}
        resource='pulsar:function:function'
        getOptionRequests={getOptionRequests}
        getDiffNode={getDiffNode}
        doRefresh={doRefresh}
        clusterName={clusterName}
        functionType='function'
      />
    );
  });

  const loadData = useMemoizedFn(async (params = {}) => {
    const { tenant, namespace } = params;
    const query = { ...params, clusterName };
    if (namespace) {
      query.namespace = namespace.replace(`${tenant}/`, ''); // 剔除 租户/ 打头
    }
    const { data } = await getFunctionList(query);
    setData(data);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <TNItems clusterName={clusterName} />
        <Form.FormItem name='functionName'>
          <Input placeholder='请输入名称' clearable />
        </Form.FormItem>
      </>
    ),
    operators: (
      <PermWrapper resource='pulsar:function:function:create'>
        <Button theme='primary' onClick={() => setVisible(true)}>
          创建任务
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch, clusterName]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <FormModal
        request={createFunction}
        visible={visible}
        handleClose={handleClose}
        clusterName={clusterName}
      />
    </Root>
  );
}

const Root = styled(ShadowCard)`
  margin-top: 12px;
`;

const Block = styled.div`
  width: 100%;
  margin-top: 6px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 1;
  border-radius: 6px;
  background-color: #fafafa;
  padding: 20px;
  .t-form__item {
    margin-bottom: 10px;
    margin-right: 0;
  }
`;
